import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box, CssBaseline, Collapse, Drawer, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, Typography, Popover,
  Divider, useTheme, Tooltip, Switch
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useLayout } from '../contexts/LayoutContext';
import { useNotifications } from '../contexts/NotificationContext';
import { Badge, LinearProgress } from '@mui/material';

const drawerWidth = 256;
const collapsedWidth = 68;

/* ── Icons ── */
const DashboardIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="6" height="6" rx="1.5"/><rect x="11" y="3" width="6" height="6" rx="1.5"/><rect x="3" y="11" width="6" height="6" rx="1.5"/><rect x="11" y="11" width="6" height="6" rx="1.5"/></svg>;
const AccountsIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2L3 6v8l7 4 7-4V6l-7-4z" strokeLinejoin="round"/><path d="M10 10v8M3 6l7 4 7-4" strokeLinejoin="round"/></svg>;
const SecurityIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2L3 5v5c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V5l-7-3z" strokeLinejoin="round"/></svg>;
const FinOpsIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 14l4-4 3 3 7-7" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 6h4v4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const BotIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="6" width="12" height="10" rx="2"/><circle cx="8" cy="11" r="1.5"/><circle cx="12" cy="11" r="1.5"/><path d="M10 3v3M7 3h6" strokeLinecap="round"/></svg>;
const SupportIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10" cy="10" r="7"/><path d="M8 8a2 2 0 013.5 1.5c0 1.5-2 2-2 2M10 14.5v.01" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const AdminIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2l1.5 3.2 3.5.5-2.5 2.5.6 3.5L10 10l-3.1 1.7.6-3.5L5 5.7l3.5-.5L10 2z" strokeLinejoin="round"/><path d="M4 15.5c0-2.5 2.7-4 6-4s6 1.5 6 4" strokeLinecap="round"/></svg>;
const AIAssistantIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2a4 4 0 014 4v1h1a3 3 0 013 3v4a3 3 0 01-3 3H5a3 3 0 01-3-3v-4a3 3 0 013-3h1V6a4 4 0 014-4z" strokeLinejoin="round"/><circle cx="7.5" cy="12" r="1.25" fill="currentColor"/><circle cx="12.5" cy="12" r="1.25" fill="currentColor"/></svg>;
const SunIcon = () => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10" cy="10" r="4"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.9 4.9l1.4 1.4M13.7 13.7l1.4 1.4M4.9 15.1l1.4-1.4M13.7 6.3l1.4-1.4" strokeLinecap="round"/></svg>;
const MoonIcon = () => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M17 12.5A7.5 7.5 0 117.5 3 5.5 5.5 0 0017 12.5z" strokeLinejoin="round"/></svg>;
const LogoutIcon = () => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M7 17H4a1 1 0 01-1-1V4a1 1 0 011-1h3M14 14l3-3m0 0l-3-3m3 3H7" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const BellIcon = () => <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2a6 6 0 016 6v3l1.5 2.5H2.5L4 11V8a6 6 0 016-6z" strokeLinejoin="round"/><path d="M8 15.5a2 2 0 004 0" strokeLinecap="round"/></svg>;
const CostOverviewIcon = () => <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M2 16l4-4 3 2 4-5 5-3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const OpportunitiesIcon = () => <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10 2l2 4h4l-3 3 1 5-4-2-4 2 1-5-3-3h4l2-4z" strokeLinejoin="round"/></svg>;
const ResourceBreakdownIcon = () => <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 4h12M4 8h8M4 12h10M4 16h6" strokeLinecap="round"/></svg>;
const InventoryIcon = () => <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="14" height="14" rx="2"/><path d="M3 7h14M7 7v10" strokeLinecap="round"/></svg>;
const ChevronRight = () => <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const CollapseIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><polyline points="15 8 12 12 15 16"/></svg>;
const ExpandIcon = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/><polyline points="13 8 16 12 13 16"/></svg>;
const MenuIcon = () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 5h14M3 10h14M3 15h14" strokeLinecap="round"/></svg>;

const ALL_MENU_ITEMS = [
  { text: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
  { text: 'AWS Accounts', icon: AccountsIcon, path: '/aws-accounts' },
  { text: 'CSPM', icon: SecurityIcon, path: '/security', featureKey: 'cspm' },
  { text: 'FinOps', icon: FinOpsIcon, path: '/cost-optimization', featureKey: 'finops', children: [
    { text: 'Cost Overview', icon: CostOverviewIcon, path: '/cost-optimization' },
    { text: 'Opportunities', icon: OpportunitiesIcon, path: '/cost-opportunities' },
    { text: 'Cost Per Resource', icon: ResourceBreakdownIcon, path: '/resource-breakdown' },
    { text: 'Inventory', icon: InventoryIcon, path: '/inventory' },
  ]},
  { text: 'Astra Compliance', icon: BotIcon, path: '/regulatory-bot', featureKey: 'regulatory_bot' },
  { text: 'Astra Cloud', icon: AIAssistantIcon, path: '/ai-assistant', featureKey: 'ai_assistant' },
  { text: 'Control Plane', icon: AdminIcon, path: '/control-plane-admin', adminOnly: true },
  { text: 'Support', icon: SupportIcon, path: '/support', featureKey: 'support' },
];

/* ── ShellKode logo mark (shared) ── */
/* ShellKode mark — uses real logo asset, icon portion only via clip */
const ShellKodeMark = ({ size = 28 }) => (
  <img
    src="/shellkode_logo.svg"
    alt="ShellKode"
    style={{ height: size, width: 'auto', display: 'block' }}
  />
);

function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const { user, logout } = useAuth();
  const { sidebarCollapsed, setSidebarCollapsed } = useLayout();
  const { mode, toggleColorMode } = useCustomTheme();
  const { notifications, clearAllNotifications, getActiveScans } = useNotifications();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'admin';

  const activeScans = getActiveScans ? getActiveScans() : [];
  const unreadCount = notifications.filter(n => !n.read).length;

  const featureFlags = user?.featureFlags || {};

  const filteredMenuItems = ALL_MENU_ITEMS.filter(item => {
    if (item.adminOnly) return isAdmin;
    if (item.featureKey && !isAdmin) return featureFlags[item.featureKey] !== false;
    return true;
  });

  const currentWidth = sidebarCollapsed ? collapsedWidth : drawerWidth;
  const activeBlue = mode === 'light' ? '#0D6EFD' : '#4D9FFF';
  const activeBg = mode === 'light' ? '#DBEAFE' : '#1A2D50';
  const hoverBg = mode === 'light' ? '#EBF3FF' : '#1A2D50';
  const sidebarBg = mode === 'light' ? '#F0F4F8' : '#0D1525';

  /* ── Nav item renderer ── */
  const NavItem = ({ item }) => {
    const isSelected = location.pathname === item.path;
    const hasChildren = item.children?.length > 0;
    const isChildSelected = hasChildren && item.children.some(c => location.pathname === c.path);
    const isExpanded = expandedSections[item.text] || isChildSelected;
    const active = isSelected && !hasChildren;

    return (
      <>
        <ListItem disablePadding sx={{ mb: '1px' }}>
          <ListItemButton
            selected={active}
            component={hasChildren ? 'div' : RouterLink}
            to={hasChildren ? undefined : item.path}
            onClick={() => {
              if (hasChildren) {
                setExpandedSections(prev => ({ ...prev, [item.text]: !prev[item.text] }));
              } else {
                setMobileOpen(false);
              }
            }}
            sx={{
              borderRadius: '8px', py: '7px', px: '10px', minHeight: 38,
              bgcolor: active ? activeBg : 'transparent',
              '&:hover': { bgcolor: active ? activeBg : hoverBg },
              '&.Mui-selected': { bgcolor: activeBg, '&:hover': { bgcolor: activeBg } },
              textDecoration: 'none',
            }}
          >
            <ListItemIcon sx={{
              minWidth: 30,
              color: (active || isChildSelected) ? activeBlue : 'text.secondary',
              display: 'flex', alignItems: 'center',
            }}>
              {item.icon && React.createElement(item.icon)}
            </ListItemIcon>
            <ListItemText primary={item.text} primaryTypographyProps={{
              fontSize: '13px',
              fontWeight: (active || isChildSelected) ? 600 : 450,
              fontFamily: "'Inter', sans-serif",
              color: (active || isChildSelected) ? activeBlue : 'text.primary',
              letterSpacing: '0.01em',
            }} />
            {hasChildren && (
              <Box sx={{
                color: 'text.secondary', display: 'flex', alignItems: 'center',
                transform: isExpanded ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.2s',
              }}>
                <ChevronRight />
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding sx={{ pl: '18px', mb: '2px' }}>
              {item.children.map(child => {
                const childActive = location.pathname === child.path;
                return (
                  <ListItem key={child.text} disablePadding sx={{ mb: '1px' }}>
                    <ListItemButton
                      selected={childActive}
                      component={RouterLink}
                      to={child.path}
                      onClick={() => setMobileOpen(false)}
                      sx={{
                        borderRadius: '8px', py: '6px', px: '10px', minHeight: 34,
                        bgcolor: childActive ? activeBg : 'transparent',
                        '&:hover': { bgcolor: childActive ? activeBg : hoverBg },
                        '&.Mui-selected': { bgcolor: activeBg, '&:hover': { bgcolor: activeBg } },
                        textDecoration: 'none',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 26, color: childActive ? activeBlue : 'text.secondary', display: 'flex', alignItems: 'center' }}>
                        {child.icon && React.createElement(child.icon)}
                      </ListItemIcon>
                      <ListItemText primary={child.text} primaryTypographyProps={{
                        fontSize: '12.5px',
                        fontWeight: childActive ? 600 : 400,
                        fontFamily: "'Inter', sans-serif",
                        color: childActive ? activeBlue : 'text.secondary',
                      }} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  /* ── Expanded sidebar ── */
  const expandedDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: sidebarBg }}>

      {/* Top: logo + collapse button */}
      <Box sx={{ px: '14px', pt: '14px', pb: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <img src="/shellkode_logo.svg" alt="ShellKode" style={{ height: 22, width: 'auto' }} />
        <Tooltip title="Collapse sidebar" placement="right">
          <IconButton
            onClick={() => setSidebarCollapsed(true)}
            size="small"
            sx={{ color: 'text.secondary', borderRadius: '7px', p: '5px', '&:hover': { bgcolor: hoverBg, color: 'text.primary' } }}
          >
            <CollapseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mx: '14px', mb: '8px' }} />

      {/* Nav */}
      <List sx={{ flex: 1, px: '10px', py: 0, overflowY: 'auto' }}>
        {filteredMenuItems.map(item => <NavItem key={item.text} item={item} />)}
      </List>

      {/* User section */}
      <Box sx={{ px: '10px', pb: '12px', pt: '8px', borderTop: `1px solid ${theme.palette.divider}` }}>
        <Box
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          sx={{
            display: 'flex', alignItems: 'center', gap: '10px',
            p: '9px 10px', borderRadius: '9px', cursor: 'pointer',
            transition: 'background 0.15s',
            '&:hover': { bgcolor: hoverBg },
          }}
        >
          {/* Avatar with notification badge */}
          <Box sx={{ position: 'relative', flexShrink: 0 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: mode === 'light' ? '#DBEAFE' : '#1A2D50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: activeBlue, fontSize: '13px', fontWeight: 700,
              fontFamily: "'Inter', sans-serif",
            }}>
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </Box>
            {unreadCount > 0 && (
              <Box sx={{
                position: 'absolute', top: -3, right: -3,
                width: 16, height: 16, borderRadius: '50%',
                bgcolor: '#EF4444', color: '#fff',
                fontSize: '9px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${sidebarBg}`,
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, fontFamily: "'Inter', sans-serif", color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.company_name || 'Account'}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email || ''}
            </Typography>
          </Box>
          <Box sx={{ color: 'text.secondary', display: 'flex' }}>
            <ChevronRight />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  /* ── Collapsed sidebar (icon rail) ── */
  const collapsedDrawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: sidebarBg }}>
      {/* Expand button at top */}
      <Box sx={{ pt: '14px', pb: '10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        <Tooltip title="Expand sidebar" placement="right" arrow>
          <IconButton
            onClick={() => setSidebarCollapsed(false)}
            size="small"
            sx={{ color: 'text.secondary', borderRadius: '8px', p: '6px', '&:hover': { bgcolor: hoverBg, color: 'text.primary' } }}
          >
            <ExpandIcon />
          </IconButton>
        </Tooltip>
        {/* Logo mark only when collapsed */}
        <Box sx={{ width: 28, height: 28, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src="/shellkode_logo.svg" alt="ShellKode" style={{ height: 22, width: 'auto', minWidth: 120, marginLeft: -2 }} />
        </Box>
      </Box>

      <Divider sx={{ width: '80%', mb: '6px' }} />

      {/* Nav icons */}
      <List sx={{ flex: 1, px: '6px', py: 0, width: '100%' }}>
        {filteredMenuItems.map(item => {
          const hasChildren = item.children?.length > 0;
          const isActive = hasChildren
            ? item.children.some(c => location.pathname === c.path)
            : location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: '1px' }}>
              <Tooltip title={item.text} placement="right" arrow>
                <ListItemButton
                  selected={isActive}
                  component={RouterLink}
                  to={hasChildren ? item.children[0].path : item.path}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    borderRadius: '9px', minHeight: 40, justifyContent: 'center', px: 0,
                    bgcolor: isActive ? activeBg : 'transparent',
                    '&:hover': { bgcolor: isActive ? activeBg : hoverBg },
                    '&.Mui-selected': { bgcolor: activeBg, '&:hover': { bgcolor: activeBg } },
                    textDecoration: 'none',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0, color: isActive ? activeBlue : 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.icon && React.createElement(item.icon)}
                  </ListItemIcon>
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      {/* Collapsed user avatar */}
      <Box sx={{ pb: '12px', pt: '8px', borderTop: `1px solid ${theme.palette.divider}`, width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Tooltip title={user?.email || 'Account'} placement="right" arrow>
          <Box
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            sx={{
              position: 'relative', width: 34, height: 34, borderRadius: '50%', cursor: 'pointer',
              bgcolor: mode === 'light' ? '#DBEAFE' : '#1A2D50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: activeBlue, fontSize: '13px', fontWeight: 700, fontFamily: "'Inter', sans-serif",
              '&:hover': { boxShadow: `0 0 0 3px ${hoverBg}` },
            }}
          >
            {user?.email?.charAt(0).toUpperCase() || 'U'}
            {unreadCount > 0 && (
              <Box sx={{
                position: 'absolute', top: -3, right: -3,
                width: 15, height: 15, borderRadius: '50%',
                bgcolor: '#EF4444', color: '#fff', fontSize: '9px', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `2px solid ${sidebarBg}`,
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </Box>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: 'background.default', minHeight: '100vh', fontFamily: "'Inter', sans-serif" }}>
      <CssBaseline />

      {/* ── User menu popover (notifications + theme + logout) ── */}
      <Popover
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 260, borderRadius: '12px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            overflow: 'hidden',
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: '16px', py: '14px', bgcolor: mode === 'light' ? '#F5F7FA' : '#0D1525' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              bgcolor: mode === 'light' ? '#DBEAFE' : '#1A2D50',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: activeBlue, fontSize: '14px', fontWeight: 700, fontFamily: "'Inter', sans-serif",
            }}>
              {user?.email?.charAt(0).toUpperCase() || 'U'}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, fontFamily: "'Inter', sans-serif", color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.company_name || 'Account'}
              </Typography>
              <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', fontFamily: "'Inter', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.email || ''}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Notifications row */}
        <Box sx={{ px: '16px', py: '10px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '6px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Box sx={{ color: 'text.secondary', display: 'flex' }}><BellIcon /></Box>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 500, fontFamily: "'Inter', sans-serif", color: 'text.primary' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Box sx={{ px: '6px', py: '1px', borderRadius: '10px', bgcolor: '#EF4444', color: '#fff', fontSize: '10px', fontWeight: 700, fontFamily: "'Inter', sans-serif" }}>
                  {unreadCount}
                </Box>
              )}
            </Box>
            {unreadCount > 0 && (
              <Typography
                onClick={clearAllNotifications}
                sx={{ fontSize: '11px', color: activeBlue, cursor: 'pointer', fontFamily: "'Inter', sans-serif", '&:hover': { textDecoration: 'underline' } }}
              >
                Clear all
              </Typography>
            )}
          </Box>

          {/* Active scans */}
          {activeScans.length > 0 && activeScans.slice(0, 2).map(scan => (
            <Box key={scan.id} sx={{ mb: '6px', p: '8px', bgcolor: mode === 'light' ? '#EBF3FF' : '#1A2D50', borderRadius: '7px' }}>
              <Typography sx={{ fontSize: '11.5px', fontWeight: 600, fontFamily: "'Inter', sans-serif", color: 'text.primary', mb: '4px' }}>{scan.title}</Typography>
              <LinearProgress variant={scan.progress ? 'determinate' : 'indeterminate'} value={scan.progress || 0} sx={{ height: 4, borderRadius: 2 }} />
            </Box>
          ))}

          {/* Recent notifications */}
          {notifications.length === 0 && activeScans.length === 0 ? (
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontFamily: "'Inter', sans-serif", py: '4px' }}>No notifications</Typography>
          ) : (
            notifications.slice(0, 3).map(n => (
              <Box key={n.id} sx={{ py: '5px', borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none' } }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 500, fontFamily: "'Inter', sans-serif", color: 'text.primary' }}>{n.title}</Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.secondary', fontFamily: "'Inter', sans-serif" }}>{n.message}</Typography>
              </Box>
            ))
          )}
        </Box>

        <Divider />

        {/* Theme toggle row */}
        <Box sx={{ px: '16px', py: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Box sx={{ color: 'text.secondary', display: 'flex' }}>
              {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            </Box>
            <Typography sx={{ fontSize: '12.5px', fontWeight: 500, fontFamily: "'Inter', sans-serif", color: 'text.primary' }}>
              {mode === 'dark' ? 'Light mode' : 'Dark mode'}
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={mode === 'dark'}
            onChange={toggleColorMode}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: activeBlue },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: activeBlue },
            }}
          />
        </Box>

        <Divider />

        {/* Logout */}
        <Box
          onClick={() => { logout(); navigate('/login'); setUserMenuAnchor(null); }}
          sx={{
            px: '16px', py: '11px', display: 'flex', alignItems: 'center', gap: '8px',
            cursor: 'pointer', transition: 'background 0.15s',
            '&:hover': { bgcolor: mode === 'light' ? '#FEE2E2' : '#2A0F0F' },
          }}
        >
          <Box sx={{ color: '#EF4444', display: 'flex' }}><LogoutIcon /></Box>
          <Typography sx={{ fontSize: '12.5px', fontWeight: 500, fontFamily: "'Inter', sans-serif", color: '#EF4444' }}>
            Sign out
          </Typography>
        </Box>
      </Popover>

      {/* Mobile hamburger */}
      <IconButton
        onClick={() => setMobileOpen(!mobileOpen)}
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed', top: 12, left: 12, zIndex: theme.zIndex.drawer + 2,
          bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '8px', p: '7px',
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Sidebar nav */}
      <Box component="nav" sx={{ width: { sm: currentWidth }, flexShrink: { sm: 0 }, transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)' }}>
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth } }}
        >
          {expandedDrawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: currentWidth,
              transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
              overflowX: 'hidden',
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: sidebarBg,
            },
          }}
          open
        >
          {sidebarCollapsed ? collapsedDrawer : expandedDrawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: '16px', sm: '24px' },
          pt: { xs: '56px', sm: '24px' },
          width: { sm: `calc(100% - ${currentWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          transition: 'width 0.22s cubic-bezier(0.4,0,0.2,1)',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default Layout;
